import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass, X, Swatches, ClipboardText, Package, Users, ArrowRight,
} from '@phosphor-icons/react';
import api from '../../lib/api';

/**
 * Cmd+K global palette — searches Master Styles, POs, Lots, Contractors.
 * Triggered from TopBar search button or Ctrl/Cmd+K.
 */
export default function GlobalSearchPalette({ open, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ styles: [], pos: [], lots: [], contractors: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const runSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setResults({ styles: [], pos: [], lots: [], contractors: [] }); return; }
    setLoading(true);
    const lower = query.toLowerCase();
    try {
      const [s, po, l, c] = await Promise.allSettled([
        api.get('/master-styles'),
        api.get('/purchase-orders'),
        api.get('/lots'),
        api.get('/contractors'),
      ]);
      const styles = (s.value?.data || []).filter(x =>
        (x.style_code || '').toLowerCase().includes(lower) ||
        (x.style_name || '').toLowerCase().includes(lower)).slice(0, 6);
      const pos = (po.value?.data || []).filter(x =>
        (x.po_number || '').toLowerCase().includes(lower) ||
        (x.client_name || '').toLowerCase().includes(lower)).slice(0, 6);
      const lots = (l.value?.data || []).filter(x =>
        (x.lot_number || '').toLowerCase().includes(lower) ||
        (x.style_code || '').toLowerCase().includes(lower)).slice(0, 6);
      const contractors = (c.value?.data || []).filter(x =>
        (x.name || '').toLowerCase().includes(lower)).slice(0, 6);
      setResults({ styles, pos, lots, contractors });
    } catch (e) { console.debug('[caught]', e); /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => runSearch(q), 220);
    return () => clearTimeout(t);
  }, [q, open, runSearch]);

  useEffect(() => {
    if (open) setQ('');
  }, [open]);

  const go = (path) => { onClose(); navigate(path); };

  if (!open) return null;
  const totalCount = results.styles.length + results.pos.length + results.lots.length + results.contractors.length;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose} data-testid="search-palette">
      <div className="w-full max-w-xl bg-white dark:bg-stone-900 rounded-2xl shadow-2xl border border-stone-200 dark:border-stone-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100 dark:border-stone-800">
          <MagnifyingGlass size={18} className="text-stone-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            placeholder="Search styles, POs, lots, contractors…"
            className="flex-1 bg-transparent border-0 focus:outline-none text-sm text-stone-800 dark:text-stone-100 placeholder-stone-400" />
          <kbd className="hidden sm:inline-block text-[10px] px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">ESC</kbd>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={16} /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.length < 2 && (
            <div className="p-8 text-center text-stone-400 text-xs">
              Type at least 2 characters to search across your data.
            </div>
          )}
          {q.length >= 2 && loading && (
            <div className="p-8 text-center text-stone-400 text-xs animate-pulse">Searching…</div>
          )}
          {q.length >= 2 && !loading && totalCount === 0 && (
            <div className="p-8 text-center text-stone-400 text-xs">
              No results for "{q}".
            </div>
          )}

          <Group icon={Swatches} title="Master Styles" rows={results.styles}
            keyFor={(r) => r.id} primary={(r) => r.style_code} secondary={(r) => r.style_name}
            onPick={(r) => go('/master-styles')} />
          <Group icon={ClipboardText} title="Purchase Orders" rows={results.pos}
            keyFor={(r) => r.id} primary={(r) => r.po_number} secondary={(r) => r.client_name || r.brand_name}
            onPick={() => go('/purchase-orders')} />
          <Group icon={Package} title="Lots" rows={results.lots}
            keyFor={(r) => r.id} primary={(r) => r.lot_number} secondary={(r) => r.style_code}
            onPick={() => go('/lots')} />
          <Group icon={Users} title="Contractors" rows={results.contractors}
            keyFor={(r) => r.id} primary={(r) => r.name} secondary={(r) => r.phone || ''}
            onPick={() => go('/contractors')} />
        </div>
      </div>
    </div>
  );
}

function Group({ icon: Icon, title, rows, keyFor, primary, secondary, onPick }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border-b border-stone-100 dark:border-stone-800 last:border-0">
      <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-stone-400 flex items-center gap-1.5">
        <Icon size={12} weight="fill" /> {title}
      </p>
      {rows.map(r => (
        <button key={keyFor(r)} onClick={() => onPick(r)}
          className="w-full flex items-center justify-between px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition text-left">
          <div className="min-w-0">
            <p className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">{primary(r)}</p>
            {secondary(r) && <p className="text-[11px] text-stone-500 truncate">{secondary(r)}</p>}
          </div>
          <ArrowRight size={14} className="text-stone-400 shrink-0" />
        </button>
      ))}
    </div>
  );
}
